import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { vendorService } from '../services/api';

// In-memory cache for resolved SEO metadata to eliminate redundant network requests
const seoCache = new Map();

/**
 * Helper to safely create or update a meta tag in document.head
 */
const setMetaTag = (selectorAttr, selectorValue, content) => {
  if (!content) return;
  try {
    let el = document.querySelector(`meta[${selectorAttr}="${selectorValue}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(selectorAttr, selectorValue);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  } catch (_) {}
};

/**
 * Helper to safely create or update the canonical link tag
 */
const setCanonicalLink = (href) => {
  if (!href) return;
  try {
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  } catch (_) {}
};

/**
 * Helper to inject or update JSON-LD structured schema script
 */
const setJsonLdSchema = (jsonString) => {
  const SCRIPT_ID = 'tii-dynamic-jsonld-schema';
  try {
    let el = document.getElementById(SCRIPT_ID);
    if (!jsonString || !jsonString.trim()) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('script');
      el.id = SCRIPT_ID;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = jsonString;
  } catch (_) {}
};

/**
 * Applies SEO tags into DOM
 */
const applySeoTags = (seoData) => {
  if (!seoData) return;

  // 1. Browser Tab Title
  if (seoData.meta_title) {
    document.title = seoData.meta_title;
  }

  // 2. Standard Search Meta Tags
  setMetaTag('name', 'description', seoData.meta_description);
  setMetaTag('name', 'keywords', seoData.meta_keywords);
  setMetaTag('name', 'robots', seoData.robots || 'index, follow');

  // 3. Canonical Link
  if (seoData.canonical_url) {
    setCanonicalLink(seoData.canonical_url);
  }

  // 4. Open Graph Social Tags (Facebook, WhatsApp, LinkedIn)
  const ogTitle = seoData.og_title || seoData.meta_title;
  const ogDesc = seoData.og_description || seoData.meta_description;
  const ogImg = seoData.og_image || 'https://vendor.topinteriorsindia.com/TII-Favicon.jpg';
  const ogUrl = seoData.canonical_url || window.location.href;

  setMetaTag('property', 'og:title', ogTitle);
  setMetaTag('property', 'og:description', ogDesc);
  setMetaTag('property', 'og:image', ogImg);
  setMetaTag('property', 'og:url', ogUrl);
  setMetaTag('property', 'og:type', 'website');
  setMetaTag('property', 'og:site_name', 'Top Interiors India');

  // 5. Twitter Card Tags
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', ogTitle);
  setMetaTag('name', 'twitter:description', ogDesc);
  setMetaTag('name', 'twitter:image', ogImg);

  // 6. JSON-LD Schema (if available)
  if (seoData.schema_json) {
    setJsonLdSchema(seoData.schema_json);
  } else {
    setJsonLdSchema(null);
  }
};

/**
 * SEOHead Component:
 * Observes client-side React Router location changes and synchronizes the
 * browser document head in real-time with zero blocking impact on user experience.
 */
const SEOHead = () => {
  const location = useLocation();
  const currentPathRef = useRef('');

  useEffect(() => {
    const rawPath = location.pathname.toLowerCase();
    const isFilterablePage =
      rawPath === '/listing' ||
      rawPath === '/interior-designers-and-architects';

    let resolvedPath = rawPath;
    if (isFilterablePage && location.search) {
      const searchParams = new URLSearchParams(location.search);
      const cleanParams = new URLSearchParams();
      // Keep only significant SEO filter parameters
      ['category', 'categoryname', 'city', 'state', 'tag', 'brand', 'tagids', 'brandids'].forEach((k) => {
        const val = searchParams.get(k);
        if (val && val.trim()) cleanParams.set(k, val.trim());
      });
      cleanParams.sort();
      const queryStr = cleanParams.toString();
      if (queryStr) {
        resolvedPath = `${rawPath}?${queryStr}`;
      }
    }

    currentPathRef.current = resolvedPath;

    // Fast-path: Check memory cache first for 0ms tag application
    if (seoCache.has(resolvedPath)) {
      applySeoTags(seoCache.get(resolvedPath));
      return;
    }

    // Resolve dynamic metadata from backend resolver
    let isCancelled = false;

    vendorService
      .resolveSeoMetadata(resolvedPath)
      .then((res) => {
        if (isCancelled || currentPathRef.current !== resolvedPath) return;
        const data = res?.data || res;
        if (data && data.meta_title) {
          seoCache.set(resolvedPath, data);
          applySeoTags(data);
        }
      })
      .catch((err) => {
        // Safe silent fallback: never crash page on network error
        console.debug('[SEOHead] Could not resolve SEO for', resolvedPath, err);
      });

    return () => {
      isCancelled = true;
    };
  }, [location.pathname, location.search]);

  return null;
};

export default SEOHead;
